import CMUserProfile from '@src/components/Atoms/CMUserProfile';
import WithdrawalModal from '@src/components/Organism/Modal/WithdrawalModal';
import authApiList from '@src/services/auth';
import { INITIAL_PROFILE } from '@src/data/Auth/profile';
import useAuth from '@src/hooks/useAuth';
import useOutsideClick from '@src/hooks/useOutsideClick';
import profileAtom from '@src/recoils/atom/profile';
import { devices } from '@src/styles/theme';
import { deleteCookie } from 'cookies-next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

const ProfileWrap = () => {
  const router = useRouter();

  const [profile, setProfile] = useRecoilState(profileAtom);
  const { fetchProfile } = useAuth();
  const { data: userInfo } = fetchProfile();

  const [toggle, setToggle] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);

  const onToggle = useCallback((value: boolean) => {
    setToggle(value);
  }, []);

  // 성공(2xx)일 때만 로그아웃 처리. fetch 래퍼가 4xx/5xx도 throw하지 않으므로 status로 판정한다.
  const handleWithdraw = async (
    reason?: string,
    detail?: string
  ): Promise<boolean> => {
    try {
      const res: any = await authApiList.withdraw(reason, detail);
      const status = res?.status;
      const ok = typeof status === 'number' && status >= 200 && status < 300;
      if (ok) {
        deleteCookie('X-AUTH-TOKEN');
        setProfile(INITIAL_PROFILE);
      }
      return ok;
    } catch {
      return false;
    }
  };

  const handleWithdrawDone = () => {
    setShowWithdrawal(false);
    router.push('/');
  };

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => setToggle(false));

  const handleLogout = () => {
    deleteCookie('X-AUTH-TOKEN');
    setProfile(INITIAL_PROFILE);
    onToggle(false);
  };

  useEffect(() => {
    if (userInfo) {
      setProfile({
        isLoggedIn: true,
        name: userInfo.displayName,
        src: userInfo.profileImageUrl,
        status: userInfo.status,
      });
    } else setProfile(INITIAL_PROFILE);
  }, [userInfo]);

  // 가입 미완(TEMP) 계정 유도.
  // (1) 초기 진입/새로고침: TEMP면 가입 완료 화면으로 보낸다.
  useEffect(() => {
    if (userInfo?.status === 'TEMP' && !router.pathname.startsWith('/auth')) {
      router.replace('/auth/login/depth2');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  // (2) 헤더 클릭 등 클라이언트 이동은 "이동 시작 시점"에 중단시켜, 대상 페이지가 떴다가
  //     되돌아오는 깜빡임 없이 가입 화면에 머무르게 한다. (/auth 흐름은 허용해 루프 방지)
  useEffect(() => {
    const blockIfTemp = (url: string) => {
      const path = url.split('?')[0] || '';
      if (userInfo?.status === 'TEMP' && !path.startsWith('/auth')) {
        if (!router.asPath.split('?')[0].startsWith('/auth/login/depth2')) {
          router.replace('/auth/login/depth2');
        }
        router.events.emit('routeChangeError');
        // Next.js 라우팅 중단용 throw (콘솔 경고는 정상 동작)
        throw 'TEMP account: 가입 완료 필요 — navigation blocked';
      }
    };
    router.events.on('routeChangeStart', blockIfTemp);
    return () => router.events.off('routeChangeStart', blockIfTemp);
  }, [userInfo, router]);

  return (
    <>
      {profile.isLoggedIn ? (
        <Wrap ref={menuRef} onClick={() => onToggle(!toggle)}>
          <CMUserProfile
            name={profile.name}
            width={devices.tablet ? '35px' : '20px'}
            height={devices.tablet ? '35px' : '20px'}
            borderRadius={devices.tablet ? '35px' : '20px'}
            src={profile.src}
          />
          <ProfileName>{profile.name}님</ProfileName>

          {toggle && (
            <Menu>
              <MenuItem
                onClick={() => {
                  router.push('/my?category=profile');
                }}
              >
                마이페이지
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleLogout();
                }}
              >
                로그아웃
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setShowWithdrawal(true);
                  onToggle(false);
                }}
              >
                회원 탈퇴
              </MenuItem>
            </Menu>
          )}
        </Wrap>
      ) : (
        <LoginText onClick={() => router.push('/auth')}>
          <DeskTopLoginText>
            <span>로그인</span>
            <Circle />
            <span>회원가입</span>
          </DeskTopLoginText>
          <TabletLoginText>로그인</TabletLoginText>
        </LoginText>
      )}
      {showWithdrawal && (
        <WithdrawalModal
          onClose={() => setShowWithdrawal(false)}
          onConfirm={handleWithdraw}
          onDone={handleWithdrawDone}
        />
      )}
    </>
  );
};

const DeskTopLoginText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
  @media ${devices.tablet} {
    display: none;
  }
`;

const TabletLoginText = styled.div`
  display: none;
  /* 모바일 헤더에는 햄버거만 있고 로그인 진입점이 없어 추가 (데스크톱은 DeskTopLoginText) */
  @media ${devices.tablet} {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    border-radius: 16px;
    border: 1px solid #ff4651;
    background: #fff1ed;
    color: #ff4651;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    text-decoration: none;
  }
`;

const Circle = styled.div`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.commonColor.gray003};
  width: 4.5px;
  height: 4.5px;
  @media ${devices.tablet} {
    display: none;
  }
`;

const LoginText = styled.span`
  @media ${devices.tablet} {
    display: block;
    color: ${({ theme }) => theme.commonColor.gray006};
    text-decoration: underline;
  }
`;

const ProfileName = styled.div`
  @media ${devices.tablet} {
    font-size: 10px;
  }
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
  @media ${devices.tablet} {
    gap: 10px;
  }
`;

const Menu = styled.div`
  display: block;
  position: absolute;
  top: 40px;
  left: 0px;
  width: 100%;
  background-color: #f1f1f1;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  z-index: 99999999999;
`;

const MenuItem = styled.li`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 44px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.commonColor.pink004};
    color: #fff;
  }
  &:first-child {
    border-radius: 10px 10px 0px 0px;
  }

  &:last-child {
    border-radius: 0px 0px 10px 10px;
  }
`;

export default ProfileWrap;
